import { Typography } from '@mui/joy';
import { ChatCircleText, ShareNetwork } from '@phosphor-icons/react';
import { CellValueChangedEvent, StatusPanelDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { Breadcrumbs, Button, PageTitle } from 'core/components';
import { AccessPermissions, TableLayoutType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow, IBreadcrumbsItem } from 'core/types';
import { goTo } from 'core/utils';
import { oldBomPartListPath } from 'jobs/routes/routePaths';
import { useCostDecimals } from 'pricing/hooks';
import { projectDetailsPath } from 'projects/routes/routePaths';
import {
  aggFuncs,
  getDefaultColDef,
  getOpenLineRowClassRules,
  getRowClass,
  isRowSelectable,
  QuotePartInlineActionDrawer,
  StatusBar,
  useTableInlineActions,
} from 'quotes/components/QuotePartList';
import { VendorQuoteDetailsButton } from 'quotes/components/VendorQuoteDetails/VendorQuoteDetailsButton';
import {
  QuoteEntityType,
  QuoteStatus,
  QuoteStatusLabel,
  QuoteType,
  QuoteVersionType,
} from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { useGetVendorQuoteParts, useQuotePartActions } from 'quotes/hooks';
import {
  IQuotePart,
  IVendorQuote,
  IVendorQuotePartListRequestPayload,
  IVendorQuotePartRow,
} from 'quotes/types';
import { getQuoteVersionNumber } from 'quotes/utils';
import { Color, Level, Variant } from 'theme/enums';

import { columnDef } from './columns';
import { QuotePartListTable } from './Table';
import { QuotePartUpsertLine } from '../QuotePartUpsertLine';
import { VendorQuotePartDetails } from '../VendorQuotePartDetails';

interface IProps {
  quote: IVendorQuote;
  quoteEntityType?: QuoteEntityType;
}

const VENDOR_QUOTE_PART_ENTITY_TYPE = 'Line';

export const VendorQuotePartList = ({ quote, quoteEntityType = QuoteEntityType.QUOTE }: IProps) => {
  const { t } = useTranslation();

  const {
    activeInlineActionDrawer,
    openInlineActionDrawer,
    onCloseInlineActionDrawer,
    editLineId,
  } = useTableInlineActions();

  const rowClassRules = useMemo(() => getOpenLineRowClassRules(editLineId), [editLineId]);

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const costDecimals = useCostDecimals();
  const { upsertQuotePart } = useQuotePartActions();
  const queryClient = useQueryClient();

  const hasVendorQuoteEditPermission = hasPermission(
    AccessPermissions.ACCESS_COMPANY_VENDOR_QUOTE_EDIT
  );

  // The Edit Line column's own gate.
  const isLineEditable = useCallback(
    (quotePart: IQuotePart) =>
      VendorQuoteHelper.isQuotePartEditLineAllowed({
        quote,
        quoteEntityType,
        hasVendorQuoteEditPermission,
        quotePart: quotePart as IVendorQuotePartRow,
      }),
    [hasVendorQuoteEditPermission, quote, quoteEntityType]
  );

  const payload: IVendorQuotePartListRequestPayload = {
    filters: { 'quoteVersion.type': QuoteVersionType.RELEASE },
    quoteId: quote?.id,
  };

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetVendorQuoteParts(payload, { fetchAllPages: true });

  const tableId = useMemo(() => `vendor${quoteEntityType}PartListTable`, [quoteEntityType]);

  const layoutType = useMemo(
    () =>
      QuoteEntityType.QUOTE === quoteEntityType
        ? TableLayoutType.VENDOR_QUOTE_PART
        : TableLayoutType.VENDOR_ORDER_PART,
    [quoteEntityType]
  );

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ quote }),
    [quote]
  );

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(
    () => ({
      statusPanels: [
        {
          statusPanel: StatusBar,
          statusPanelParams: {
            entityType: VENDOR_QUOTE_PART_ENTITY_TYPE,
            quoteId: quote?.id,
            quoteVersionType: QuoteVersionType.RELEASE,
            isDataLoadCompleted: loadingPercentage === 100,
            companySettings: user?.companySettings,
            freightEnabled: quote?.freightEnabled,
            quoteEntityType,
            quoteType: QuoteType.VENDOR,
          },
          key: 'customerQuoteStatusBar',
          align: 'left',
        },
      ],
    }),
    [loadingPercentage, quote?.freightEnabled, quote?.id, quoteEntityType, user?.companySettings]
  );

  const columnDefs = useMemo(
    () =>
      columnDef({
        openInlineActionDrawer,
        hasTaxonomyColumn:
          isAlternateVersionEnabled && Boolean(user?.companySettings?.taxonomyEnabled),
        quoteEntityType,
        costDecimals,
        quote,
        hasVendorQuoteEditPermission,
      }),
    // Depends on the status, not the quote object: every 'quote' refetch (Send to FileNet, Submit
    // Vendor Order) hands us a new object identity, and rebuilding columnDefs resets the user's
    // unsaved column layout. The editability predicates read only the status.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      openInlineActionDrawer,
      isAlternateVersionEnabled,
      user?.companySettings?.taxonomyEnabled,
      quoteEntityType,
      costDecimals,
      quote?.status,
      hasVendorQuoteEditPermission,
    ]
  );

  const defaultColDef = useMemo(() => getDefaultColDef(), []);

  const onCellValueChanged = useCallback(
    async (_: any, event: CellValueChangedEvent): Promise<{ updatedRow: AgGridDataRow }> => {
      const field = event.colDef.field;
      const newValue = event.newValue;

      const { success, error, quotePart } = await upsertQuotePart({
        quotePart: {
          id: event.data.id,
          partId: event.data.partId,
          [field]: newValue,
        },
      });

      if (!success) {
        throw new Error(error);
      }

      // A line edit can flip trade_agreement_status Sent -> Updated Post Sent server-side, with no
      // signal in this response, so refetch the quote or the chip and Send as SPA gate go stale.
      queryClient.invalidateQueries(['quote']);

      return { updatedRow: quotePart };
    },
    [queryClient, upsertQuotePart]
  );

  const onEditLineSubmitSuccess = useCallback(() => {
    // Same server-side flip as the inline cell edits: a line save can move trade_agreement_status
    // Sent -> Updated Post Sent with no signal in the response. The grid row itself is already
    // replaced locally by the upsert drawer.
    queryClient.invalidateQueries(['quote']);
  }, [queryClient]);

  const breadcrumbs: IBreadcrumbsItem[] = [
    {
      label: t('labelProject'),
      text: quote?.customerProjectName,
      to: projectDetailsPath(quote?.customerProjectId),
    },
    {
      label: t('labelJob'),
      text: quote?.jobName,
      onClick: () => goTo(oldBomPartListPath(quote?.customerProjectId, quote?.bomId)),
    },
    {
      text: quote?.vendorContactGroupName,
    },
  ];

  const buttons = useMemo(
    () => [
      <VendorQuoteDetailsButton key={'details'} quote={quote} quoteEntityType={quoteEntityType} />,
      <Button
        key={'messages'}
        color={Color.PRIMARY}
        variant={Variant.OUTLINED}
        icon={<ChatCircleText />}
        hide
      />,
      <Button
        key={'share'}
        color={Color.PRIMARY}
        variant={Variant.OUTLINED}
        icon={<ShareNetwork />}
        hide
      />,
    ],
    [quote, quoteEntityType]
  );

  const chips = useMemo(() => {
    if (QuoteEntityType.QUOTE === quoteEntityType) {
      return null;
    }

    if (QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER === quote?.status) {
      return [
        { label: QuoteStatusLabel[quote?.status], color: Color.PRIMARY, variant: Variant.SOLID },
      ];
    }
  }, [quote?.status, quoteEntityType]);

  const subtitle = quote?.vendorProjectName ? (
    <Trans
      i18nKey="subtitleVendorQuoteDetails"
      values={{
        projectName: quote?.vendorProjectName,
      }}
      components={{
        valueWrapper: <Typography level={Level.TITLE_MD} />,
      }}
    />
  ) : null;

  return (
    <AgGridClientTableProvider
      tableId={tableId}
      entityType={VENDOR_QUOTE_PART_ENTITY_TYPE}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      statusBar={statusBar}
      defaultColDef={defaultColDef}
      aggFuncs={aggFuncs}
      getRowClass={useCallback(
        (params) => getRowClass({ ...params, quoteEntityType }),
        [quoteEntityType]
      )}
      rowClassRules={rowClassRules}
      onCellValueChanged={onCellValueChanged}
      layoutType={layoutType}
      hasDefaultStatusBar={false}
      hasLayoutActions
      headerCheckbox={loadingPercentage === 100}
      isRowSelectable={isRowSelectable}
    >
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <PageTitle
        title={t('titleVendorQuote', {
          id: quote?.id,
          version: getQuoteVersionNumber(quote),
          entityType: quoteEntityType,
        })}
        chips={chips}
        subtitle={subtitle}
        buttons={buttons}
        wrapperStyle={{ pb: '16px' }}
      />

      <VendorQuotePartDetails
        isOpen={QuotePartInlineActionDrawer.DETAILS === activeInlineActionDrawer}
        onClose={onCloseInlineActionDrawer}
      />

      <QuotePartUpsertLine
        quote={quote}
        quoteType={QuoteType.VENDOR}
        isOpen={QuotePartInlineActionDrawer.EDIT_LINE === activeInlineActionDrawer}
        onClose={onCloseInlineActionDrawer}
        onSubmitSuccess={onEditLineSubmitSuccess}
        entityType={quoteEntityType}
        isLineEditable={isLineEditable}
      />

      <QuotePartListTable quote={quote} quoteEntityType={quoteEntityType} />
    </AgGridClientTableProvider>
  );
};
